<template>
  <div
    v-if="flashcard"
    ref="flashcard-view-container"
    class="grouping-quiz-flashcard-component"
  >
    <template v-if="reviewing && flashcardState">
      <flashcard-header
        :answered="true"
        :breadcrumbs="breadcrumbs"
        :grouping="grouping"
        :flashcard="flashcard"
        :show-progress="true"
      />
      <flashcard
        :answered="true"
        :answer-ids="flashcardState.answerIds"
        :correct="flashcardState.correct"
        :flashcard="flashcard"
        :raw-answer-data="flashcardState.rawAnswerData"
        @set-answer-ids="setAnswerIds"
        @set-raw-answer-data="setRawAnswerData"
      />
      <mastery-footer
        :disable-btn="false"
        :primary-action-text="primaryFooterActionText"
        secondary-action-text="Back to List"
        @primary-action="handlePrimaryFooterAction"
        @secondary-action="routeToGroupingList"
      />
    </template>
    <template v-else>
      <flashcard-header
        :answered="answered"
        :breadcrumbs="breadcrumbs"
        :grouping="grouping"
        :flashcard="flashcard"
        :show-progress="true"
      />
      <flashcard
        :answered="answered"
        :answer-ids="answerIds"
        :correct="correct"
        :flashcard="flashcard"
        :raw-answer-data="rawAnswerData"
        @set-answer-ids="setAnswerIds"
        @set-raw-answer-data="setRawAnswerData"
      />
      <mastery-footer
        :disable-btn="footerActionDisabled"
        :primary-action-text="primaryFooterActionText"
        @primary-action="handlePrimaryFooterAction"
      />
    </template>
  </div>
</template>

<script>
import Flashcard from '@/components/flashcards/flashcard';
import FlashcardHeader from '@/components/flashcards/header';
import MasteryFooter from '@/components/mastery/footer';

import FlashcardViewMixin from '@/mixins/flashcardViews';

import { rateLimitingEnabled } from '@/utils';

export default {
  name: 'GroupingQuizFlashcard',
  components: { Flashcard, FlashcardHeader, MasteryFooter },
  mixins: [FlashcardViewMixin],
  props: {
    assetAssociation: {
      type: Object,
    },
    breadcrumbs: {
      type: Array,
    },
    flashcards: {
      type: Array,
    },
    flashcardStates: {
      type: Array,
    },
    grouping: {
      type: Object,
    },
    reviewing: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    flashcard() {
      return this.flashcards.find(flashcard => flashcard.id === this.assetAssociation.assetBId);
    },
    flashcardState() {
      return this.flashcardStates.find(state => state.flashcardId === this.flashcard.id);
    },
    footerActionDisabled() {
      return !this.hasBeenAnswered;
    },
    nextAssetAssociation() {
      const currentAAIndex = this.grouping.sortedAssetAssociations.findIndex(aa => aa.id === this.assetAssociation.id);
      return this.grouping.sortedAssetAssociations[currentAAIndex + 1];
    },
    primaryFooterActionText() {
      if (this.nextAssetAssociation) {
        return this.reviewing ? 'Next' : 'Submit';
      }
      return 'Finish';
    },
  },
  mounted() {
    if (this.reviewing && !this.flashcardState) {
      this.$router.push({
        name: 'groupings#show',
        params: {
          categoryId: this.$route.params.categoryId,
          groupingId: this.grouping.id,
        },
      });
    }
  },
  methods: {
    handleAnswer() {
      this.correct = this.evaluateAnswer(this.flashcard, this.answerIds, this.rawAnswerData);
      const params = {
        assetAssociationId: this.assetAssociation.id,
        correct: this.correct,
        flashcardId: this.flashcard.id,
        flashcardStatusId: this.$store.getters['flashcardStatuses/greenFlashcardStatus'].id,
        rateLimited: rateLimitingEnabled({
          store: this.$store,
          apptimize: this.$apptimize,
          analytics: this.$analytics,
        }),
      };

      this.createFlashcardState(params);
    },
    handlePrimaryFooterAction() {
      if (!this.reviewing) {
        this.handleAnswer();
      }

      if (this.nextAssetAssociation) {
        this.routeToNextFlashcard();
      } else {
        this.routeToGroupingList();
      }
    },
    handleSecondaryFooterAction() {
      this.routeToGroupingList();
    },
    routeToGroupingList() {
      this.$router.push({
        name: 'groupings#show',
        params: {
          categoryId: this.$route.params.categoryId || 'all',
          groupingId: this.grouping.id,
        },
      });
    },
    routeToNextFlashcard() {
      let route = {
        name: 'groupings#show',
        params: {
          categoryId: this.$route.params.categoryId || 'all',
          groupingId: this.grouping.id,
          assetAssociationId: this.nextAssetAssociation.id,
        },
      };
      if (this.reviewing) {
        route.query = this.$route.query;
      } else {
        this.resetFlashcard();
      }
      this.$router.push(route);
    },
  },
};
</script>

<style lang="scss" scoped>
.grouping-quiz-flashcard-component {
  :deep(.flashcard-component-container) {
    margin-bottom: rem-calc(64);
  }
}
</style>
